/**
 * Cart Drawer Behavior Enhancement
 * Ensures cart drawer refreshes properly when items are deleted
 * Shows empty state when cart becomes empty
 */

(function() {
  'use strict';

  let previousCartCount = null;


  /**
   * Force refresh cart drawer content
   */
  function refreshCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (!cartDrawer) return;

    const cartItemsComponent = cartDrawer.querySelector('.cart-items-component');
    if (!cartItemsComponent) return;

    const sectionId = cartItemsComponent.getAttribute('data-section-id');

    // Try using theme's section renderer first (only if the section wrapper
    // actually exists — the drawer is a snippet, so it usually does not)
    if (window.sectionRenderer && document.getElementById(`shopify-section-${sectionId}`)) {
      window.sectionRenderer.renderSection(sectionId, { cache: false })
        .catch(error => {
          console.error('Error refreshing cart drawer:', error);
          // Fallback to manual refresh
          manualRefreshCartDrawer(sectionId, cartItemsComponent);
        });
    } else {
      // Manual refresh if sectionRenderer not available
      manualRefreshCartDrawer(sectionId, cartItemsComponent);
    }
  }

  /**
   * Manual refresh fallback — re-fetch the current page and swap the drawer's
   * cart items ("/?section_id=cart-drawer" is not a real section and 404s)
   */
  function manualRefreshCartDrawer(sectionId, cartItemsComponent) {
    fetch(window.location.href, { cache: 'no-store' })
      .then(response => response.text())
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newContent = doc.querySelector('.cart-items-component');
        
        if (newContent && cartItemsComponent.parentElement) {
          cartItemsComponent.outerHTML = newContent.outerHTML;
        }
      })
      .catch(error => console.error('Manual refresh failed:', error));
  }

  /**
   * Check if we're on the home page
   */

  /**
   * Handle cart count changes
   */
  function handleCartCountChange(itemCount) {
    // The drawer markup branches on `cart.empty?` at render time, so it has to be
    // re-fetched whenever the cart CROSSES the empty boundary - in either
    // direction. This previously only handled non-empty -> empty, so a page
    // loaded with an empty cart kept its empty-state drawer even after items
    // were added (the bubble count updates via header-cart-count.js, a separate
    // path, which is why the count looked right while the drawer looked empty).
    const wasEmpty = previousCartCount === 0;
    const isEmpty = itemCount === 0;

    if (previousCartCount !== null && wasEmpty !== isEmpty) {
      setTimeout(() => {
        refreshCartDrawer();

        /* Deliberately NOT closing when the cart empties. Removing the last item
           is the moment the empty state earns its place — it offers this week's
           sessions and a gift card, which is the whole reason to have one. The
           drawer used to dismiss itself on the home page half a second later,
           throwing that away and leaving the shopper staring at the page they
           were already on, unsure the delete had even worked. */
      }, 300);
    }

    // Update previous count
    previousCartCount = itemCount;
  }

  /**
   * Listen for cart updates and refresh drawer when cart becomes empty
   */
  function initCartDrawerBehavior() {
    // Initialize previous count from current cart state
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        previousCartCount = cart.item_count;
      })
      .catch(() => {
        // Silently fail - will initialize on next cart interaction
      });

    // Listen for cart update events
    document.addEventListener('theme:cart:update', (event) => {
      const itemCount = event.detail?.data?.item_count;
      if (itemCount !== undefined) {
        handleCartCountChange(itemCount);
      } else {
        // If no item count in event, fetch it
        fetch('/cart.js')
          .then(response => response.json())
          .then(cart => handleCartCountChange(cart.item_count))
          .catch(error => console.error('Error fetching cart:', error));
      }
    });

    // Also listen for cart:count-updated custom event (from header-cart-count.js)
    document.addEventListener('cart:count-updated', (event) => {
      const itemCount = event.detail?.itemCount;
      if (itemCount !== undefined) {
        handleCartCountChange(itemCount);
      }
    });

  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartDrawerBehavior);
  } else {
    initCartDrawerBehavior();
  }
})();
